import { MILESTONES } from "../core/constants";
import { activeSlotOf, emptySave, parseSave, serialize } from "../core/save";
import type { SaveFile, Slot } from "../core/save";
import { REPLAY_VERSION } from "../core/constants";
import type { ReplayCommand } from "../core/replay";
import type { GameState } from "../core/types";

/**
 * The impure half of saving. Everything that touches localStorage, the clock,
 * crypto or the DOM lives here; core/save.ts does the thinking.
 */

const SAVE_KEY = "umschlag.save";

/** localStorage can be unavailable (private mode) or full. */
let lastError: string | null = null;

/** What the save menu shows when a write failed. Null while all is well. */
export const saveError = (): string | null => lastError;

function fail(err: unknown): void {
  lastError =
    err instanceof DOMException && err.name === "QuotaExceededError"
      ? "Der Speicher ist voll. Lösche eine Welt, dann wird wieder gesichert."
      : "Speichern nicht möglich — das Spiel läuft, sichert aber nicht.";
  console.warn(lastError, err);
}

function persist(save: SaveFile): void {
  try {
    localStorage.setItem(SAVE_KEY, serialize(save));
    lastError = null;
  } catch (err) {
    fail(err);
  }
}

/**
 * Never null: a missing, corrupt or unknown-version save yields an empty one.
 *
 * Anything that did not come back byte-identical is written straight back —
 * a migrated save, or a fresh empty one. Without that, the ids generated here
 * would differ on every single read.
 */
/** `?seed=<0..2^32-1>` from a query string, else null (RFC-0047). */
export function seedFromQuery(search: string): number | null {
  const raw = new URLSearchParams(search).get("seed");
  if (raw === null || !/^\d{1,10}$/.test(raw)) return null;
  const seed = Number(raw);
  return seed < 2 ** 32 ? seed : null;
}

/**
 * A fresh map seed. Impure, hence here and not in core — src/core/ takes its
 * randomness as an argument, exactly as it takes ids and timestamps.
 *
 * Witness evidence builds (VITE_WITNESS=1, RFC-0047) may pin it with `?seed=`.
 * Vite replaces the flag at build time, so any other build compiles the query
 * away rather than carrying a switch players could flip.
 */
export const newSeed = (): number =>
  (import.meta.env.VITE_WITNESS === "1" ? seedFromQuery(location.search) : null) ??
  Math.floor(Math.random() * 2 ** 32);

export function readSave(): SaveFile {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(SAVE_KEY);
  } catch (err) {
    fail(err);
  }

  const parsed = raw === null ? null : parseSave(raw, crypto.randomUUID());
  const save = parsed ?? emptySave(crypto.randomUUID(), Date.now());
  if (serialize(save) !== raw) persist(save);
  return save;
}

/** Read, transform, write. Every mutation below is one line over this. */
export function updateSave(fn: (save: SaveFile) => SaveFile): SaveFile {
  const next: SaveFile = { ...fn(readSave()), updatedAt: Date.now() };
  persist(next);
  return next;
}

export function listSlots(): Slot[] {
  // Reversed before sorting: a stable sort keeps array order on a tie, and a tie
  // is the normal case — "Neue Welt" stamps the world it leaves and appends the
  // new one in the same millisecond. Later in the array means newer.
  return [...readSave().slots].reverse().sort((a, b) => b.updatedAt - a.updatedAt);
}

export const activeSlot = (): Slot | null => activeSlotOf(readSave());

/**
 * "Tag 24 · Umschlag". Player-facing, hence German, hence not in core.
 *
 * Leads with the furthest milestone rather than the route count: how far a
 * world got is what tells two of them apart, and the route count on a fresh
 * one is always the same number.
 */
export function defaultSlotName(world: GameState): string {
  const last = world.milestones.at(-1);
  const reached = MILESTONES.find((m) => m.id === last);
  return `Tag ${world.day} · ${reached?.label ?? "Anfang"}`;
}

// ─── Slot operations ────────────────────────────────────────────────────────

/**
 * Writes into the named slot and stamps it. No-op if that slot is gone.
 *
 * The slot is named, never "whichever is active": switching worlds activates the
 * new slot and then reloads, and the unload autosave of the world just left would
 * land in the fresh one and overwrite it.
 */
export function writeWorld(world: GameState, slotId: string): void {
  updateSave((save) => ({
    ...save,
    slots: save.slots.map((s) => (s.id === slotId ? { ...s, world, updatedAt: Date.now() } : s)),
  }));
}

/** Appends a slot, makes it active, returns its id. */
export function createSlot(world: GameState, name?: string): string {
  const id = crypto.randomUUID();
  updateSave((save) => ({
    ...save,
    activeSlot: id,
    slots: [...save.slots, { id, name: name || defaultSlotName(world), updatedAt: Date.now(), kind: "snapshot", world }],
  }));
  return id;
}

/** Creates a new compact replay slot from a seed. */
export function createReplaySlot(world: GameState, name?: string): string {
  const id = crypto.randomUUID();
  updateSave((save) => ({
    ...save,
    activeSlot: id,
    slots: [...save.slots, {
      id, name: name || defaultSlotName(world), updatedAt: Date.now(), kind: "replay",
      replayVersion: REPLAY_VERSION, seed: world.seed, tick: -1, commands: [], world,
    }],
  }));
  return id;
}

/**
 * Saves commands for a replay slot; legacy snapshot slots stay untouched.
 *
 * The seed and the command list are the whole save — the live world is passed in
 * only to keep the slot's cached copy current for the menu labels. Autosave
 * deliberately does not verify that replaying them reproduces it: that check
 * costs a full reconstruction of the game so far, once per game day, and by day
 * 500 that is 3.6 s of frozen main thread every six seconds of play. Determinism
 * is proven in test/replay.test.ts, where it costs nothing per player.
 */
export function writeReplay(slotId: string, commands: readonly ReplayCommand[], tick: number, world: GameState): void {
  updateSave((save) => ({
    ...save,
    slots: save.slots.map((slot) => slot.id === slotId && slot.kind === "replay"
      ? { ...slot, commands, tick, world, updatedAt: Date.now() }
      : slot),
  }));
}

export function renameSlot(id: string, name: string): void {
  updateSave((save) => ({
    ...save,
    slots: save.slots.map((s) =>
      // Blank restores the generated label — a nameless world is unfindable.
      s.id === id ? { ...s, name: name.trim() || defaultSlotName(s.world) } : s,
    ),
  }));
}

export function deleteSlot(id: string): void {
  updateSave((save) => ({
    ...save,
    // Dropping the active slot leaves none active; the next load starts fresh.
    activeSlot: save.activeSlot === id ? null : save.activeSlot,
    slots: save.slots.filter((s) => s.id !== id),
  }));
}

export function activateSlot(id: string): void {
  updateSave((save) => (save.slots.some((s) => s.id === id) ? { ...save, activeSlot: id } : save));
}

// ─── Export / import ────────────────────────────────────────────────────────

export const slugify = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "welt";

/** Downloads one slot as a save file containing exactly that slot. */
export function exportSlot(id: string): void {
  const save = readSave();
  const slot = save.slots.find((s) => s.id === id);
  if (!slot) return;

  const single: SaveFile = { ...save, activeSlot: slot.id, slots: [slot] };
  const url = URL.createObjectURL(new Blob([serialize(single)], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `umschlag-${slugify(slot.name)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Adds the file's slots under fresh ids and returns the last one, or null if the
 * file is not a save. Fresh ids because trusting the file's would make a second
 * import a silent overwrite, and would let a hand-edited file collide two slots.
 * The file's profile id is ignored — the identity belongs to the device.
 */
export async function importSlotFile(file: File): Promise<string | null> {
  let incoming: SaveFile | null = null;
  try {
    incoming = parseSave(await file.text(), crypto.randomUUID());
  } catch (err) {
    fail(err);
    return null;
  }
  if (!incoming || incoming.slots.length === 0) return null;

  const added: Slot[] = incoming.slots.map((s) => ({ ...s, id: crypto.randomUUID() }));
  const last = added[added.length - 1]!;
  updateSave((save) => ({ ...save, activeSlot: last.id, slots: [...save.slots, ...added] }));
  return last.id;
}
